import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { consume, ContextType } from '@lit/context';
import { Task } from '@lit/task';
import { SlInput } from '@shoelace-style/shoelace';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { api } from '../api.js';
import { contexts } from '../contexts.js';
import { docLinkStyles } from './cssstyles.js';
import { RepositoryItem } from './pick-repository.js';

export type GitlabPat = {
  owner: string,
  repo: string,
  accessToken: string,
}

function toItem(project: WireTypes.GitlabProject): RepositoryItem {
  return {
    id: `${project.owner}/${project.name}`,
    prefix: project.owner + '/',
    name: project.name,
    avatarUrl: project.avatarUrl,
    avatarShape: 'rounded',
    isPrivate: project.isPrivate,
  };
}

@customElement('pick-gitlab-pat')
class PickGitlabPat extends LitElement {
  @consume({ context: contexts.serverInfo, subscribe: true }) private _serverInfo?: ContextType<typeof contexts.serverInfo>;
  @query('sl-input') private _tokenInput?: SlInput;

  @state() private _token = '';

  private _projectsTask = new Task(this, {
    args: () => [this._token] as const,
    task: async ([token], { signal }): Promise<{ invalidToken: boolean, truncated: boolean, projects: WireTypes.GitlabProject[] }> => {
      if (!token)
        return { invalidToken: false, truncated: false, projects: [] };
      return await api.projectSources.listGitlabProjects.POST({
        accessToken: token,
      }, { signal });
    },
  });

  private _select(projects: WireTypes.GitlabProject[], event: CustomEvent<RepositoryItem>) {
    const project = projects.find(candidate => toItem(candidate).id === event.detail.id);
    if (!project)
      return;
    this._emit({
      owner: project.owner,
      repo: project.name,
      accessToken: this._token,
    });
  }

  private _onTokenInput() {
    const token = this._tokenInput?.value.trim() ?? '';
    if (token === this._token)
      return;
    this._token = token;
    // Any project picked earlier was picked with the previous token, so retract
    // it. Otherwise the form stays submittable with credentials the user has
    // already replaced.
    this._emit(undefined);
  }

  private _emit(gitlabPat: GitlabPat|undefined) {
    this.dispatchEvent(new CustomEvent<GitlabPat|undefined>('gitlab-pat', {
      detail: gitlabPat,
    }));
  }

  override render() {
    const gitlabOrigin = this._serverInfo?.gitlabOrigin ?? 'https://gitlab.com';
    const createTokenUrl = `${gitlabOrigin.replace(/\/+$/, '')}/-/user_settings/personal_access_tokens?name=Flakiness.io&scopes=read_api,self_rotate`;
    return html`
      <v-box>
        <sl-input
          required
          @sl-input=${() => this._onTokenInput()}
          @paste=${() => setTimeout(() => this._onTokenInput(), 0)}
          type=password
          placeholder="Access Token with read_api scope"
          password-toggle
        >
          <sl-icon slot=prefix name=gitlab></sl-icon>
        </sl-input>
        <div class=hint>
          <a-ext href=${createTokenUrl}>Create a token</a-ext>
          with the <code>read_api</code> scope. With the <code>self_rotate</code>
          scope as well, Flakiness.io rotates the token before it expires.
        </div>
        ${this._token ? this._renderProjects() : nothing}
      </v-box>
    `;
  }

  private _renderProjects() {
    return this._projectsTask.render({
      pending: () => html`<h-box><sl-spinner></sl-spinner> Loading projects...</h-box>`,
      error: () => html`<h-box>Something went wrong; please try again later.</h-box>`,
      complete: ({ invalidToken, truncated, projects }) => invalidToken ? html`
        <fk-callout variant="danger">
          GitLab rejected this token. Check that it is correct and has the
          <code>read_api</code> scope.
        </fk-callout>
      ` : !projects.length ? html`
        <fk-callout variant="warn">
          No projects found. This token needs <b>Reporter</b> access or above to
          a project.
        </fk-callout>
      ` : html`
        <v-box>
          <pick-repository
            .items=${projects.map(toItem)}
            .icon=${'gitlab'}
            .placeholder=${'Choose your GitLab project'}
            @repository-picked=${this._select.bind(this, projects)}
          ></pick-repository>
          ${truncated ? html`
            <div class=hint>
              Showing the ${projects.length} most recently active projects.
            </div>
          ` : nothing}
        </v-box>
      `,
    });
  }

  static styles = [docLinkStyles, css`
    :host {
      display: block;
    }

    .hint {
      color: var(--sl-color-neutral-500);
      font-size: var(--sl-font-size-small);
    }
  `];
}
